import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Award, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';

const HomePage = () => {
  const studio = {
    name: "Studio Prism Entertainment",
    tagline: "Illuminate the unseen.",
    description:
      "A long-standing and cutting-edge Idol studio where many celebrities have launched their careers. Known for innovative approaches and testing new methods in artist development.",
    features: [
      "Long-standing industry presence with proven track record",
      "Cutting-edge training methodologies and facilities",
      "Alumni network of successful celebrities and idols",
      "Innovative approach: Seoul-based groups proving themselves in smaller cities",
      "Talent recruitment from diverse geographical locations",
      "Studio-as-sanctuary environment for artist development",
    ],
    celebrities: [
      "Luna Star - International K-pop sensation",
      "Phoenix Rising - Chart-topping boy group",
      "Crystal Dreams - Award-winning girl group",
      "Solo artist Kai Eclipse - Billboard success",
      "Harmony Collective - Multi-platinum group",
    ],
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-midnight-ink via-midnight-ink/95 to-midnight-ink text-white">
      <section className="relative overflow-hidden py-20 lg:py-32">
        <div className="absolute inset-0 bg-gradient-to-r from-prism-violet/10 via-transparent to-peach-blush/5" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-8">
            <div className="flex justify-center mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_multi-brand-portal/artifacts/dkrj21tk_Studio%20Prism%20Entertainment%20Logo.png"
                alt="Studio Prism Entertainment"
                className="h-32 w-auto hover-lift"
              />
            </div>
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                <span className="bg-gradient-to-r from-prism-violet to-iridescent-silver bg-clip-text text-transparent">
                  Studio Prism
                </span>
                <br />
                <span className="text-white">Studio Prism</span>
              </h1>
              <p className="text-xl lg:text-2xl text-white font-medium">{studio.tagline}</p>
              <p className="max-w-3xl mx-auto text-lg text-white leading-relaxed">{studio.description}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              <Button asChild size="lg" className="bg-gradient-to-r from-prism-violet to-prism-violet/80 hover:from-prism-violet/90 hover:to-prism-violet text-white border-0 shadow-lg hover:shadow-prism-violet/25 hover:scale-105 transition-all duration-300">
                <Link to="/Prism/apply" className="flex items-center gap-2">
                  Apply Now <ArrowRight size={18} />
                </Link>
              </Button>
              <Button variant="outline" size="lg" className="border-white/30 text-white hover:bg-white/5 hover:text-white hover:border-white/50 transition-all duration-300">
                <Link to="/Prism/contract">Contract</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[['15+','Years Experience'],['200+','Artists Trained'],['50+','Debut Success'],['10+','Awards Won']].map(([n,l]) => (
              <div key={n} className="text-center space-y-2">
                <div className="text-3xl lg:text-4xl font-bold text-white">{n}</div>
                <div className="text-white">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-prism-violet/10 text-prism-violet border-prism-violet/20">
              <Sparkles size={16} className="mr-2" /> About Studio Prism
            </Badge>
            <h2 className="text-3xl lg:text-4xl font-bold mb-6">Where Stars Are Born</h2>
            <p className="max-w-3xl mx-auto text-lg text-white">
              Our innovative approach combines traditional excellence with cutting-edge methods, creating the next generation of global entertainment icons.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {studio.features.map((feature, index) => (
              <Card key={index} className="bg-midnight-ink/50 border-prism-violet/20 hover:border-prism-violet/40 transition-all duration-300 hover:shadow-lg hover:shadow-prism-violet/10">
                <CardContent className="p-6 text-white">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-gradient-to-r from-prism-violet to-prism-violet/80 rounded-full flex items-center justify-center">
                      <Star size={16} className="text-white" />
                    </div>
                    <p className="leading-relaxed">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-8">Our Success Stories</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {studio.celebrities.map((celebrity, index) => (
                <Card key={index} className="bg-gradient-to-br from-prism-violet/10 to-peach-blush/5 border-prism-violet/20 hover:border-prism-violet/40 transition-all duration-300">
                  <CardContent className="p-6 text-center text-white">
                    <Award className="w-8 h-8 text-prism-violet mx-auto mb-3" />
                    <p className="font-medium">{celebrity}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;